import { createSlice } from '@reduxjs/toolkit';
import * as R from 'ramda';

// Utils
import http from 'utils/http';
import { createSnack } from 'snackbar/snackbarSlice';

const initialState = {
  currentBaseRate: null,
  currentSubscriptions: [],
  currentSubscriptionsLoading: true,
  globalCustomers: [],
  globalCustomersAffiliates: [],
  globalCustomersAffiliatesLoading: false,
  globalCustomersLoading: false,
  planChosen: '',
  subscriptionPlans: [],
  subscriptionTierDetailsLoading: false,
  subscriptionTiers: [],
};

const reducers = {
  resetSubscriptions: R.always(initialState),
  setCurrentBaseRate: R.assoc('currentBaseRate'),
  setCurrentSubscriptions: R.assoc('currentSubscriptions'),
  setCurrentSubscriptionsLoading: R.assoc('currentSubscriptionsLoading'),
  setGlobalCustomers: R.assoc('globalCustomers'),
  setGlobalCustomersAffiliates: R.assoc('globalCustomersAffiliates'),
  setGlobalCustomersAffiliatesLoading: R.assoc('globalCustomersAffiliatesLoading'),
  setGlobalCustomersLoading: R.assoc('globalCustomersLoading'),
  setPlanChosen: R.assoc('planChosen'),
  setSubscriptionPlans: R.assoc('setSubscriptionPlans'),
  setSubscriptionTierDetailsLoading: R.assoc('subscriptionTierDetailsLoading'),
  setSubscriptionTiers: R.assoc('subscriptionTiers'),
};

const { actions, reducer } = createSlice({
  initialState,
  name: 'subscriptions',
  // eslint-disable-next-line react-hooks/rules-of-hooks
  reducers: R.map(singleReducer => R.flip(R.useWith(singleReducer, [R.prop('payload')])), reducers),
});

export const {
  resetSubscriptions,
  setAffiliateVolumes,
  setCurrentBaseRate,
  setCurrentSubscriptions,
  setCurrentSubscriptionsLoading,
  setGlobalCustomers,
  setGlobalCustomersAffiliates,
  setGlobalCustomersAffiliatesLoading,
  setSubscriptionPlans,
  setSubscriptionTierDetailsLoading,
  setGlobalCustomersLoading,
  setPlanChosen,
  setSubscriptionTiers,
} = actions;

// Selectors
const mergePath = R.concat(['subscriptions']);
export const getStoreValueByProp = R.o(R.path, mergePath);

// Actions
export const fetchGlobalCustomers = () => (dispatch, getState) => {
  const { currentSubscriptions } = getState().subscriptions;
  dispatch(setGlobalCustomersLoading(true));
  http.get('subscription-plans/get-user-affiliate-global-customers').then(({ data }) => {
    const globalCustomers = R.reject(
      ({ customerId }) =>
        R.any(
          R.compose(R.propEq('customerId', customerId), R.prop('globalCompany')),
          currentSubscriptions
        ),
      data
    );

    dispatch(
      // get-user-affiliate-global-customers now passes a parameter called "hasActiveSubscription"
      // On the Front End we only want to display Global Customers without subscriptions, so the
      // data is filtered here before being set in the Redux module
      setGlobalCustomers(globalCustomers)
    );
    dispatch(setGlobalCustomersLoading(false));
  });
};

export const fetchGlobalCustomersAffiliates = () => dispatch => {
  dispatch(setGlobalCustomersAffiliatesLoading(true));
  http.get('subscription-plans/get-affiliate-volume-usage').then(({ data }) => {
    dispatch(setGlobalCustomersAffiliates(data));
    dispatch(setGlobalCustomersAffiliatesLoading(false));
  });
};

export const fetchCurrentSubscriptions = () => dispatch => {
  dispatch(setCurrentSubscriptionsLoading(true));
  http
    .get('subscription-plans/enrollments')
    .then(({ data }) => {
      dispatch(setCurrentSubscriptions(data));
    })
    // leaving out for now until enrollments endpoint is fixed to not
    // throw 500 when empty array is found
    // .catch(error => {
    //   dispatch(
    //     createSnack({
    //       message: error.response.data.title,
    //       options: { variant: 'error' },
    //     })
    //   );
    // })
    .finally(() => {
      dispatch(setCurrentSubscriptionsLoading(false));
      dispatch(fetchGlobalCustomers());
    });
};

export const fetchSubscriptionTierDetails = () => dispatch => {
  dispatch(setSubscriptionTierDetailsLoading(true));
  http.get('subscription-plans/subscription-tier-details').then(({ data }) => {
    dispatch(setSubscriptionTiers(data));
    dispatch(setSubscriptionTierDetailsLoading(false));
  });
};

const sendConfirmationEmail = params => dispatch => {
  const {
    tierNumber: subscriptionPlanTier,
    globalCustomer,
    selectedAffiliate: { dataCompanyId, companyCustomerIdName },
  } = params;

  http
    .post('subscription-plans/send-swift-plus-enrollment-email', {
      billToCompany: {
        companyCustomerIdName,
        dataCompanyId,
      },
      globalCustomer,
      subscriptionPlanTier,
    })
    .then(({ message }) =>
      dispatch(
        createSnack({
          message,
        })
      )
    )
    .catch(error =>
      dispatch(
        createSnack({
          message: error.response.data.title,
          options: { variant: 'error' },
        })
      )
    );
};

const sendCustomerEmail = params => dispatch => {
  const {
    tierNumber: subscriptionPlanTier,
    globalCustomer,
    selectedAffiliate: { dataCompanyId, companyCustomerIdName },
  } = params;

  http
    .post('subscription-plans/send-swift-plus-enrollment-customer-email', {
      billToCompany: {
        companyCustomerIdName,
        dataCompanyId,
      },
      globalCustomer,
      subscriptionPlanTier,
    })
    .then(({ message }) =>
      dispatch(
        createSnack({
          message,
        })
      )
    )
    .catch(error =>
      dispatch(
        createSnack({
          message: error.response.data.title,
          options: { variant: 'error' },
        })
      )
    );
};

// eslint-disable-next-line no-unused-vars
export const createSubscriptionPlan = params => dispatch => {
  const {
    tierNumber: subscriptionPlanTier,
    globalCustomer: { id },
    selectedAffiliate: { dataCompanyId, companyCustomerIdName },
  } = params;

  return http
    .post('subscription-plans/create-contract', {
      billToCompany: {
        companyCustomerIdName,
        dataCompanyId,
      },
      globalCustomerId: id,
      subscriptionPlanTier,
    })
    .then(({ data }) => {
      dispatch(setSubscriptionPlans(data));
      dispatch(sendConfirmationEmail(params));
      dispatch(sendCustomerEmail(params));
      return data;
    })
    .catch(error => {
      dispatch(
        createSnack({
          message: error.response.data.title,
          options: { variant: 'error' },
        })
      );
      return error;
    });
};

// eslint-disable-next-line no-unused-vars
export const fetchBaseRate = params => dispatch => {
  const {
    globalCustomer: { id },
  } = params;

  http
    .get(`subscription-plans/get-current-base-rate?globalCustomerId=${id}`)
    .then(({ data }) => {
      dispatch(setCurrentBaseRate(data));
    })
    .catch(error => {
      dispatch(
        createSnack({
          message: error.response.data.title,
          options: { variant: 'error' },
        })
      );
    });
};

export default reducer;
